<template>
  <base-layout :do-skip-header="false">
    <template #main>
      <h1 class="text-3xl md:text-4xl font-bold mt-8 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
        <span class="md:whitespace-nowrap">Pictures</span>
        <select 
          id="defaultEdition" 
          v-model="defaultEdition" 
          class="form-select w-full md:w-auto text-md flex-grow md:flex-grow-0">
          <option v-for="item in editions" :value="item" :key="item.edition">
            {{ item.name }}
          </option>
        </select>
      </h1>

      <div v-if='defaultEdition && pictureCats' class="">
          <div class="my-5">
            <h2 class="text-2xl md:text-3xl font-bold h-16 text-red-600 text-center">{{ defaultEdition.page_title }}</h2>
          </div>
          <div v-if="defaultEdition?.edition == '2024'">
            <div class="relative w-full" style="padding-top: 56.25%;"> <!-- 16:9 Aspect Ratio -->
              <iframe 
                class="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/JYyZz3kWDhE"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
            <br><br>
          </div>
          <div v-if="defaultEdition?.edition == '2025'">
            <div class="relative w-full" style="padding-top: 56.25%;"> <!-- 16:9 Aspect Ratio -->
              <iframe 
                class="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/tCgBzUAVVF8"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
            <br><br>
          </div>
        <div class="container mx-auto px-4" :class="defaultEdition?.edition == '2024' ? 'py-8' : 'py-2'">
          <div v-for="(cat, catIndex) in pictureCats" :key="cat.id" >
            <div v-if="cat.files.length > 0">
              <div class="my-8 text-center">
                <div class="bg-gradient-to-r from-red-600 to-red-700 rounded-lg py-3 px-8 inline-flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform duration-200">
                  <h2 class="text-2xl font-bold text-white tracking-wide">
                    {{ cat.name }} 
                  </h2>
                </div>
              </div>
              <!-- Photo Grid -->
              <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6">
                <!-- Show only limited images initially -->
                <div v-for="(image, index) in cat.files.slice(0, cat.visibleImages)"
                  :key="image.id || image"
                  :class="index === 0 ? 'xl:col-span-2 xl:row-span-2' : ''"
                  class="relative group cursor-pointer"
                  @click="openImageModal(image)">
                  <img :src="image" class="w-full h-full object-cover rounded-xl shadow-md" />

                  <!-- Hover effect -->
                  <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-xl"></div>
                </div>
              </div>
              <div v-if="cat.files.length > cat.visibleImages" class="flex justify-center mt-8">
              <button @click="loadMore(catIndex)"  class="bg-trandparent text-gray-400 border hover:text-gray-600 border-gray-400 px-6 py-2 rounded-full transition-colors">
                See More
              </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Image Preview Modal -->
      <div v-if="selectedImage" class="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
        @click.self="closeImageModal" @keydown.esc="closeImageModal">
        <div class="relative w-full max-w-6xl flex flex-col items-center">
          <!-- Close button -->
          <button @click="closeImageModal" class="absolute -top-12 right-0 text-white text-3xl hover:text-gray-300"
            aria-label="Close image preview"> &times; 
          </button>

          <!-- Full-size image -->
          <img :src="selectedImage" class="max-w-full max-h-[80vh] object-contain rounded-lg"/>

          <!-- Download button -->
          <button @click="downloadImage(selectedImage)" class="mt-4 bg-white text-gray-900 px-5 py-2 rounded-lg shadow-lg hover:bg-gray-200">
            Download Image
          </button>
        </div>
      </div>
    </template>
  </base-layout>
</template>

<script>
import _ from "lodash";

import BaseLayout from "../components/BaseLayout.vue";

export default {
  name: "Register",
  components: {
    BaseLayout,
  },
  data() {
    return {
      loadIncrement: 16,  
      sliderSettingsMain: {
        itemsToShow: 1,
        centerMode: false,
      },
      sliderSettingsThumb: {
        itemsToShow: 5,
        itemsToSlide: 5,
      },
      editions: JSON.parse(localStorage.getItem('editions')) || [],
      defaultEdition: null,
      pictureCats: null,
      allSettings: null,
      enablePicSharing: true,
      selectedImage: null
    }
  },
  watch: {
    defaultEdition: async function (val) {
      this.pictureCats = null // toggle loading
      if(!val){
        return;
      }
      this.pictureCats = await this.$http.get(`/api/misc/pictures/${val.edition}`)
        .then(res => {
          return res.data.payload.map(cat => {
            return {
              ...cat,
              visibleImages: 17 
            };
          });
        });

      // eslint-disable-next-line no-undef
      const urlPrefix = process.env.NODE_ENV === "development" ? "https://europeanbrokermeeting.com" : window.location.origin
      // const urlPrefix = process.env.NODE_ENV === "development" ? "https://europeanbrokermeeting.com" : window.location.origin

      _.each(this.pictureCats, (cat, i) => {
        cat.files = _.map(cat.files, file => `${urlPrefix}/${file}`)
        cat.sliderRef = `slider-${i}`
      })
    }
  },
  async created() {
    this.allSettings = await this.$http.get("/api/misc/allSettings").then(res => res.data.payload);
    this.enablePicSharing = Boolean(Number(this.allSettings.enablePicSharing));
    this.getEditions();
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleModalKeydown);
  },
  methods: {
    openImageModal(image) {
      this.selectedImage = image;
      window.addEventListener('keydown', this.handleModalKeydown);
    },
    closeImageModal() {
      this.selectedImage = null;
      window.removeEventListener('keydown', this.handleModalKeydown);
    },
    handleModalKeydown(event) {
      if (event.key === 'Escape') {
        this.closeImageModal();
      }
    },
    loadMore(catIndex) {
      this.pictureCats[catIndex].visibleImages += this.loadIncrement;
    },
    clickTo(index, sliderName) {
      this.$refs[sliderName][0].slideTo(index);
    },
    async downloadImage(imageUrl) {
      try {
        // Fetch the image
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        
        // Create a blob URL
        const blobUrl = URL.createObjectURL(blob);
        
        // Create a temporary anchor element
        const link = document.createElement('a');
        link.href = blobUrl;
        
        // Extract filename from URL or generate a unique one
        const fileName = imageUrl.split('/').pop() || `download-${Date.now()}.jpg`;
        link.download = fileName;
        
        // Programmatically click the link to trigger download
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Download failed:', error);
        // Fallback to the simple method if fetch fails
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = imageUrl.split('/').pop() || `download-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    async getEditions() {
      const res = await this.$http.get("/api/misc/editions");
      if(res.data.status === 200) {
        this.editions = res.data.editions;
        this.defaultEdition = this.editions[0] || {};
        localStorage.setItem('editions', JSON.stringify(this.editions));
      }
    },
    getFileName(url) {
      return url.split('/').pop();
    }
  }
}

function generateRandomBase16(length = 5) {
  let result = '';
  const characters = '0123456789ABCDEF';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

</script>
